import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  ChevronRight,
  ArrowLeft,
  Share2,
  Check,
  Facebook,
  Twitter,
  MessageCircle,
  BookOpen,
  MapPin,
  Sparkles,
} from "lucide-react";
import { getBlog, getBlogs, getBlogSync, getBlogsSync } from "../api";
import { renderFormattedText } from "../components/FormattedText";
import SEO from "../components/SEO";
import { buildArticleSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(() => getBlogSync(slug));
  const [related, setRelated] = useState(() =>
    getBlogsSync().filter((b) => b.slug !== slug).slice(0, 3)
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getBlog(slug)
      .then((b) => {
        if (b) {
          setBlog(b);
          getBlogs({ category: b.category })
            .then((list) => {
              setRelated(list.filter((x) => x.id !== b.id && x.slug !== slug).slice(0, 3));
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!getBlogSync(slug)) setBlog(null);
      });
  }, [slug]);

  if (blog === undefined) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center text-gray-500">Loading article...</div>;
  }

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center" data-testid="blog-not-found">
        <h1 className="text-2xl font-bold text-gray-900">Article not found</h1>
        <p className="text-gray-500 text-sm mt-2">The blog post you requested could not be found.</p>
        <Link to="/blog" className="text-[#e5243b] text-sm mt-4 inline-flex items-center gap-1.5 font-bold">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Render markdown-like sections cleanly
  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.split("\n");
    const elements = [];
    let currentParagraph = [];

    const flushParagraph = (key) => {
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={`p-${key}`} className="text-gray-700 leading-relaxed text-[16px] mb-6 break-words">
            {renderFormattedText(currentParagraph.join(" "))}
          </p>
        );
        currentParagraph = [];
      }
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushParagraph(idx);
      } else if (trimmed.startsWith("### ")) {
        flushParagraph(idx);
        elements.push(
          <h3 key={`h3-${idx}`} className="text-2xl font-black text-gray-900 tracking-tight mt-10 mb-4">
            {renderFormattedText(trimmed.replace("### ", ""))}
          </h3>
        );
      } else if (trimmed.startsWith("## ")) {
        flushParagraph(idx);
        elements.push(
          <h2 key={`h2-${idx}`} className="text-3xl font-black text-gray-900 tracking-tight mt-12 mb-5">
            {renderFormattedText(trimmed.replace("## ", ""))}
          </h2>
        );
      } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        flushParagraph(idx);
        const itemText = trimmed.replace(/^[-*]\s+/, "");
        elements.push(
          <li key={`li-${idx}`} className="text-gray-700 leading-relaxed text-[16px] ml-6 list-disc mb-2 break-words">
            {renderFormattedText(itemText)}
          </li>
        );
      } else if (trimmed.startsWith("---")) {
        flushParagraph(idx);
        elements.push(<hr key={`hr-${idx}`} className="my-8 border-gray-200" />);
      } else {
        currentParagraph.push(trimmed);
      }
    });

    flushParagraph("final");
    return elements;
  };

  const blogCanonicalUrl = `https://minigolfalabama.com/blog/${blog.slug || slug}`;
  const blogSchemas = [
    buildArticleSchema(blog, blogCanonicalUrl),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: blog.title, url: blogCanonicalUrl },
    ]),
  ].filter(Boolean);

  return (
    <article className="min-h-[70vh] bg-[#f8fafc]" data-testid="blog-post-page">
      <SEO
        title={`${blog.title} | Mini Golf Blog`}
        description={blog.excerpt || blog.content?.slice(0, 160)}
        canonicalUrl={blogCanonicalUrl}
        ogType="article"
        ogImage={blog.image}
        schema={blogSchemas}
      />
      {/* Top Header */}
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-400 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-gray-300 font-medium truncate max-w-xs">{blog.title}</span>
          </nav>

          <div className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider text-white bg-[#e5243b] rounded-full mb-4">
            {blog.category || "Mini Golf Article"}
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
            {blog.title}
          </h1>

          <div className="flex items-center gap-6 mt-6 text-xs sm:text-sm text-gray-300 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-red-500/20 text-[#e5243b] font-bold text-xs flex items-center justify-center">
                {blog.author?.[0] || "A"}
              </div>
              <span className="font-semibold text-white">{blog.author || "Editorial Staff"}</span>
            </div>
            <span className="flex items-center gap-1.5 text-gray-400">
              <Calendar className="w-4 h-4" /> {blog.publishedAt}
            </span>
            <span className="flex items-center gap-1.5 text-gray-400">
              <Clock className="w-4 h-4" /> {blog.readTime || "5 min read"}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Cover Image */}
        <div className="rounded-3xl overflow-hidden shadow-md mb-10 border border-gray-200">
          <img
            src={blog.coverImage}
            alt={blog.title}
            className="w-full h-80 sm:h-[450px] object-cover"
          />
        </div>

        {/* Article Body */}
        <div className="bg-white rounded-3xl border border-gray-200 p-8 sm:p-12 shadow-sm">
          {blog.excerpt && (
            <div className="bg-gray-50 border-l-4 border-[#e5243b] p-5 rounded-r-2xl text-gray-700 italic text-[17px] leading-relaxed mb-8">
              {blog.excerpt}
            </div>
          )}

          <div className="prose max-w-none">
            {renderContent(blog.content)}
          </div>

          {/* Social Share & Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Share this:</span>
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
                {copied ? "Link Copied!" : "Copy Link"}
              </button>
            </div>

            <Link
              to="/blog"
              className="text-xs font-bold text-[#e5243b] hover:text-[#b91c1c] inline-flex items-center gap-1 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to All Articles
            </Link>
          </div>
        </div>

        {/* Directory CTA Card */}
        <div className="bg-gradient-to-r from-[#181a20] to-[#242731] text-white rounded-3xl p-8 sm:p-10 mt-10 shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 border border-gray-800">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
              <MapPin className="w-4 h-4" /> Find Courses Near You
            </div>
            <h3 className="text-2xl font-black text-white tracking-tight">
              Ready to Play Mini Golf?
            </h3>
            <p className="text-gray-300 text-sm mt-1 max-w-lg">
              Explore hundreds of miniature golf and putt-putt courses in Alabama and across all 50 states.
            </p>
          </div>
          <Link
            to="/state/alabama"
            className="px-6 py-3.5 text-sm font-extrabold uppercase tracking-wider text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-lg transition-all whitespace-nowrap"
          >
            Browse Directory
          </Link>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="text-2xl font-black text-gray-900 tracking-tight mb-6">
              More Recommended Articles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/blog/${item.slug}`}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all group flex flex-col"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-[#e5243b] uppercase tracking-wider">
                        {item.category}
                      </span>
                      <h4 className="font-bold text-gray-900 text-sm mt-1 group-hover:text-[#e5243b] transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                    </div>
                    <span className="text-xs text-gray-500 mt-4 block">{item.readTime || "4 min read"}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogPost;
